import { useState } from "react";

type Faq = {
  question: string;
  answer: string;
};

const FAQS: readonly Faq[] = [
  {
    question: "How is this different from a one-day AI workshop?",
    answer:
      "A workshop gives your team a starting point. This is what happens after that. Staying Ahead is built around structured sessions, daily reinforcement, and content tied to how your team works, so the behaviour change sticks past day one.",
  },
  {
    question: "Do we need any prior AI experience?",
    answer:
      "No. Teams start from wherever they are. The programme is scoped to your team's actual workflows and goals, not a fixed curriculum that assumes a certain baseline.",
  },
  {
    question: "What team sizes do you work with?",
    answer: "Typically 15 to 200 people.",
  },
  {
    question: "What kinds of results should we expect?",
    answer:
      "Teams that complete the programme typically cut time on repetitive tasks by 30 to 50 percent within the first month. A team of 20 commonly reclaims 40+ hours a week collectively.",
  },
  {
    question:
      "How do we know this will work for our specific industry or workflows?",
    answer:
      "Every programme is scoped to your team's workflows before it starts. There is no generic curriculum. The sessions, the tools, and the reinforcement are all built around what your team actually does.",
  },
  {
    question: "What makes this better than an AI course platform?",
    answer:
      "Course platforms give individuals content. This gives teams capability. The live sessions, daily reinforcement, and workflow-specific focus are what drive behaviour change. Most teams that come to us have already tried a platform and found that completion rates were low and nothing really changed.",
  },
  {
    question: "How is pricing structured?",
    answer:
      "Pricing is based on team size and programme length. If you include your team size when you fill in the enquiry form, we'll include a rough number in our first response.",
  },
  {
    question: "Who runs the sessions?",
    answer:
      "Vaibhav, who has built companies including GrowthSchool and Outskill using the exact workflows, tools, and systems taught in every session. What gets taught is what his own teams use every week.",
  },
  {
    question: "What happens after we fill in the form?",
    answer:
      "We review your details and follow up within two business days. If there's a fit, we schedule a 30-minute call. A programme proposal comes within five days of that call — no commitment required to see it.",
  },
];

function PlusMinusIcon({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center text-white/70"
    >
      <span className="absolute h-px w-3 bg-current" />
      <span
        className={`absolute h-3 w-px bg-current transition-transform duration-200 ${
          open ? "rotate-90 opacity-0" : "rotate-0 opacity-100"
        }`}
      />
    </span>
  );
}

function FaqItem({
  faq,
  index,
  open,
  onToggle,
}: {
  faq: Faq;
  index: number;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02]">
      <button
        id={buttonId}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left md:px-6 md:py-5"
      >
        <span className="font-fira text-sm font-normal leading-snug text-white md:text-base">
          {faq.question}
        </span>
        <PlusMinusIcon open={open} />
      </button>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="px-4 pb-4 font-fira text-sm font-normal leading-relaxed text-[#FFFFFF85] md:px-6 md:pb-5 md:text-base"
        >
          {faq.answer}
        </div>
      ) : null}
    </div>
  );
}

export default function BusinessFaqs() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      id="business-faqs"
      className="w-full bg-black font-fira text-white py-12 md:py-20"
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between md:gap-12">
          <div className="md:w-[40%]">
            <p className="font-fira text-sm font-normal uppercase tracking-[0.18em] text-[#FFFFFF]">
              FAQs
            </p>
            <h2 className="mt-3 font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:mt-4 md:text-[42px] md:font-semibold">
              Common questions,
              <br />
              before the call
            </h2>
          </div>

          <div className="flex flex-col gap-3 md:w-[55%] md:gap-4">
            {FAQS.map((faq, i) => (
              <FaqItem
                key={faq.question}
                faq={faq}
                index={i}
                open={openIndex === i}
                onToggle={() =>
                  setOpenIndex((curr) => (curr === i ? null : i))
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
